import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AudioWaveform } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { PageLayout } from '@/components/shared/PageLayout';
import { useTranslation } from '@/hooks/useTranslation';
import { useSignalMachines } from '../hooks/useSignalMachines';
import { useInfluxSeries } from '../hooks/useInfluxSeries';
import { SignalsContextPanel } from '../components/SignalsContextPanel';
import { SignalsChartCard } from '../components/SignalsChartCard';
import type { InfluxSeriesQuery } from '../type/SignalsTypes';

export function SignalsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const machines = useSignalMachines();

  const [submittedQuery, setSubmittedQuery] = useState<InfluxSeriesQuery | null>(null);
  const series = useInfluxSeries(submittedQuery);

  return (
    <PageLayout contentClassName="max-w-5xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          breadcrumb={[
            { label: t('common.brand'), onClick: () => navigate('/') },
            { label: t('signals.page.breadcrumb') },
          ]}
          title={
            <span className="flex items-center gap-2">
              <AudioWaveform className="h-6 w-6 shrink-0 text-primary" aria-hidden />
              {t('signals.page.title')}
            </span>
          }
          description={t('signals.page.description')}
        />

        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <div className="w-full lg:w-[300px] lg:shrink-0 lg:sticky lg:top-6">
            <SignalsContextPanel
              machines={machines.data}
              machinesLoading={machines.isLoading}
              onLoad={setSubmittedQuery}
            />
          </div>

          <div className="min-w-0 flex-1">
            <SignalsChartCard
              signal={series.data ?? null}
              isLoading={series.isLoading}
              error={series.error}
              onRetry={series.retry}
              isEmptyWindow={series.isEmptyWindow}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
